import type { IntegrationErrorResponse } from '@/definitions/common'

/**
 * Body accepted by POST /api/v1/staff/{org_id}.
 *
 * The endpoint creates the organization's *first* staff member and sets
 * `user_type` to admin itself, so the role is not something this request carries
 * — and the same endpoint rejects a second call for an organization that already
 * has staff. The request schema also accepts nickname, pronoun, birthdate,
 * phone, fax, prefixes, suffixes, and credentials; the Create Org flow does not
 * collect them, so they are omitted here until something needs them.
 */
export interface CreateStaffRequest {
  name_given: string
  name_family: string
  username: string
  email: string
  /**
   * Their Keycloak login is created alongside the staff record, so this is
   * required. Keycloak forces a new one at first sign-in.
   */
  temporary_password: string
  primary_location_id?: string
  npi?: string
  license_number?: string
}

/**
 * One item from GET /api/v1/staff/{org_id}.
 *
 * Wider than the equivalent location list item: it already carries the name,
 * email, role, and account state, so a staff list renders in full from this
 * response alone and only needs the detail endpoint when someone drills in.
 *
 * `username` is nullable even though creating a staff member requires one,
 * because older records predate the requirement.
 */
export interface StaffListItem {
  id: string
  name_given: string
  name_family: string
  email: string
  username: string | null
  enabled: boolean
  user_type: string
}

/**
 * The subset of the staff detail response this app reads.
 *
 * Returned by GET /api/v1/staff/{org_id}/{staff_id}, the create endpoint, the
 * enable and disable endpoints, and the product-flags endpoint alike. It is the
 * list item widened with the rest of the record — `org_id`, `avatar`,
 * `name_full`, pronoun, phone and fax, the primary location, NPI type validity,
 * the scheduling and VOIP flags, the signature file, and the audit stamps are all
 * part of the response too, and none of them are declared here because nothing
 * reads them.
 *
 * `birthdate` is a date-only `YYYY-MM-DD` string, not a timestamp; it needs
 * `formatIsoDateNumeric` rather than the epoch-millisecond formatter.
 */
export interface StaffDetail extends StaffListItem {
  name_nickname: string | null
  birthdate: string | null
  prefixes: string | null
  suffixes: string | null
  credentials: string | null
  license_number: string | null
  npi: string | null
  /** Null on a staff member whose E-Prescribe has never been set, which means off. */
  erx_enabled: boolean | null
}

/**
 * Body accepted by PUT /api/v1/staff/{org_id}/{staff_id}/product-flags.
 *
 * `erx_enabled` is required because the endpoint replaces the flag rather than
 * patching it. api-service only acts on a value that differs from what is stored,
 * so sending the current one is accepted and does nothing.
 */
export interface UpdateStaffProductFlagsRequest {
  erx_enabled: boolean
  /**
   * The staff member whose Dosespot clinician this one should share — the same
   * person practising at two organizations. Their clinician gains this
   * organization's clinic instead of a second identity being created for them.
   * Their Dosespot clinician must be one this organization does not already hold --
   * including one already linked in from elsewhere. api-service allows a prescriber
   * identity to be held once per organization, not once per staff member.
   *
   * Read only while a clinician is being created, so the backend rejects it
   * alongside a disable and reports it as unapplied when the flag is unchanged.
   * Omitted rather than sent empty — its presence is what selects that path.
   *
   * Must be a UUID string: the endpoint declares it as a UUID and answers 422 for
   * anything else, which a bare `string` here cannot express.
   */
  erx_link_to_staff_id?: string
}

/**
 * The product-flags response: the staff member as stored, plus what did not apply.
 *
 * `integration_errors` is why this is not just a `StaffDetail` — the flag can save
 * while the Dosespot call behind it fails, and the reason arrives here rather than
 * as a failed request.
 */
export interface StaffProductFlags extends StaffDetail {
  integration_errors: IntegrationErrorResponse[]
}

/**
 * The `code` values POST /api/v1/staff/{org_id} sends beside a 409's message.
 *
 * Both refusals answer the same status and want opposite handling, so the code is
 * what tells them apart: a taken username is corrected and retried, while an
 * organization that already has staff will refuse every retry there is. Mirrors
 * the backend's `DUPLICATE_USERNAME_CODE` / `ORG_ALREADY_HAS_STAFF_CODE`.
 *
 * Only `ORG_ALREADY_HAS_STAFF` is matched on: everything else, named or not, is
 * treated as correctable, which is the safe way round. `USERNAME_TAKEN` is here
 * because half a mirror of the endpoint's codes reads as though one is missing.
 */
export const STAFF_ERROR_CODES = {
  ORG_ALREADY_HAS_STAFF: 'org_already_has_staff',
  USERNAME_TAKEN: 'username_taken',
} as const

/**
 * The `user_type` values that mean the staff member administers the practice.
 *
 * Two of them, mirroring the backend's `ADMIN_USER_TYPES`: `staff_admin` comes
 * from records whose type was derived from a granular role name, and is
 * admin-equivalent to plain `admin`. Reading either one as admin is the whole
 * point — matching only `admin` would show a practice's administrator as
 * ordinary staff.
 */
export const ADMIN_USER_TYPES: readonly string[] = ['admin', 'staff_admin']
